import React, { useState, useEffect, useRef } from 'react';
import { oneOfType, number, string } from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

// global
import Layout from '../../../Layout/StandardLayout';
import { setLoadingStatus, setSavingStatus } from '../../../../reducers/notification';

// local
import { load as loadBluesnap, init as initBluesnap } from './bluesnap';
import { updateData, getPostValues, updateDataBeforeSubmission, updateDataAfterSubmission, showErrors } from './utils';
import Errors from './Errors';
import Wizard from './Wizard';
import { AccountInfo, Agreements, BusinessDetails, CompanyRep, Owners } from './Steps';
import BluesnapFields from './BluesnapFields';
import { getBluesnapToken, addBankAccount, addSolaBankAccount } from '../apiRequests';
import usePaymentProvider from '../usePaymentProvider';
import { notificationError } from '../../../../core/actions';

/**
 * @param data      wizard form state
 * @param forSola   include the fields only Sola's boarding application needs.
 *
 * The Sola fields are gated rather than always present because BlueSnap posts this same
 * object to /v1/merchant/:id. Sending it full routing and account numbers would put
 * credentials our servers never previously received into the BlueSnap request -- that flow
 * only ever sent the last four, with its SDK capturing the rest directly.
 */
const getBusinessDetails = (data, forSola = false) => {
  const retrieve = field => data[field]?.value;
  const retrieveAddress = prefix => ({
    line1: retrieve(`${prefix}Address`),
    city: retrieve(`${prefix}City`),
    state: retrieve(`${prefix}State`),
    zip: retrieve(`${prefix}Zip`),
  });
  const retrievePerson = prefix => ({
    firstName: retrieve(`${prefix}FirstName`),
    lastName: retrieve(`${prefix}LastName`),
    dateOfBirth: retrieve(`${prefix}DateOfBirth`),
    tel: retrieve(`${prefix}Phone`),
    percent: retrieve(`${prefix}Percent`),
    address: retrieveAddress(prefix),
  });

  // Sola requires the full 9-digit SSN per signer, plus a title. The BlueSnap field holds
  // only the last four, and BlueSnap needs neither -- so these are added only for Sola.
  // The wizard does not ask for a title, and every signer it collects is an owner or the
  // company representative, so 'Owner' is accurate rather than a guess.
  const retrieveSolaPerson = prefix => ({
    ...retrievePerson(prefix),
    ssn: retrieve(`${prefix}SSN`),
    title: 'Owner',
  });

  const buildPerson = forSola ? retrieveSolaPerson : retrievePerson;
  // Only for Sola. BlueSnap posts this same object to /v1/merchant/:id, and it must not
  // start carrying full routing and account numbers -- that flow only ever sent the last
  // four, with its SDK capturing the rest directly.
  const solaOnly = {};

  if (forSola) {
    // Collected by the wizard already, but never sent under BlueSnap -- its SDK read these
    // from the DOM. Sola's application needs them in the request.
    solaOnly.taxId = retrieve('businessTaxId');
    // BlueSnap sent a hardcoded support@clientbook.com through a hidden SDK input, so the
    // wizard never asked. Sola needs the MERCHANT's own address.
    solaOnly.email = retrieve('businessEmail');
    solaOnly.businessStartDate = retrieve('businessStartDate');
    solaOnly.monthlySalesVolume = retrieve('businessMonthlySalesVolume');
    solaOnly.banking = {
      bankName: retrieve('bankName'),
      routingNumber: retrieve('bankRoutingNumber'),
      accountNumber: retrieve('bankAccountNumber'),
    };
  }

  return {
    nickname: retrieve('bankAccountNickname'),
    dba: retrieve('businessName'),
    name: retrieve('businessLegalName'),
    ...solaOnly,
    description: retrieve('businessProductAndServiceDesc'),
    tel: retrieve('businessPhone'),
    website: retrieve('businessWebsite'),
    type: retrieve('businessType'),
    averageTransactionAmount: retrieve('businessAverageTransactionAmount'),
    highestTransactionAmount: retrieve('businessHighestTransactionAmount'),
    riskMonitoring: retrieve('businessRiskMonitoring'),
    riskPayment: retrieve('businessRiskPayment'),
    riskByCommission: retrieve('businessRiskByCommission'),
    address: retrieveAddress('business'),
    representative: buildPerson('companyRep'),
    owners: ['owner1', 'owner2', 'owner3', 'owner4'].map(buildPerson).filter(o => o.firstName !== undefined),
  };
};

const setupBluesnapSuccess = (id, setData, history, onError, retrieveData) => pfToken => {
  return addBankAccount(id, {
    ...getPostValues(),
    pfToken,
    businessDetails: getBusinessDetails(retrieveData()),
  })
    .then(() => {
      updateDataAfterSubmission(setData);
      setSavingStatus(false);
      history.push('/BankAccount/list');
    })
    .catch(errors => {
      updateDataAfterSubmission(setData);
      onError(errors, setData);
      setSavingStatus(false);
    });
};

/**
 * Sola submission. Unlike BlueSnap's, this is not a callback -- there is no SDK to capture
 * anything first, so the collected form goes straight to our API in one request.
 */
const submitSolaApplication = (id, setData, history, onError, retrieveData) => {
  return addSolaBankAccount(id, {
    ...getPostValues(),
    businessDetails: getBusinessDetails(retrieveData(), true),
  })
    .then(() => {
      updateDataAfterSubmission(setData);
      setSavingStatus(false);
      history.push('/BankAccount/list');
    })
    .catch(errors => {
      updateDataAfterSubmission(setData);
      onError(errors, setData);
      setSavingStatus(false);
    });
};

const setupBluesnapError =
  (setData, onError) =>
  (errors = []) => {
    console.log('Error', { errors });
    updateDataAfterSubmission(setData);
    onError(errors, setData);
    setSavingStatus(false);
    notificationError('Bank account was not added');
  };

const AddAccount = ({ merchantId }) => {
  // null until the payments backend answers; nothing loads or renders before then.
  const provider = usePaymentProvider(merchantId);
  const isSola = provider === 'SOLA';
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState(null);
  const history = useHistory();
  // used for bluesnap errors (both FE & BE)
  const [errors, setErrors] = useState([]);

  const dataRef = useRef();
  dataRef.current = data;

  const handleErrors = (errs, set) => {
    setErrors(list => [...list, ...errs]);
    showErrors(errs, set);
    setShowAll(true);
  };

  useEffect(() => {
    // const getData = () => dataRef.current;
    // const i = setInterval(() => {
    //   try {
    //     getBusinessDetails(getData());
    //   } catch (e) {
    //     console.error('INSUFFICIENT DATA');
    //   }
    // }, 1000);
    // return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (!provider) {
      return;
    }

    setLoadingStatus(true);

    // Sola has no browser SDK for boarding -- the whole application is submitted server-side
    // -- so neither the BlueSnap script nor its token is fetched. Loading them would inject a
    // third-party script and hit a BlueSnap endpoint for a merchant that has no BlueSnap
    // account.
    if (isSola) {
      updateData({}, setData);
      setLoadingStatus(false);
      return;
    }

    // get everything we need, async
    Promise.all([getBluesnapToken(merchantId), loadBluesnap()])
      .then(([token]) => {
        // set initial values
        updateData({}, setData);

        // init bluesnap
        const getData = () => dataRef.current;
        initBluesnap(
          token,
          () => getBluesnapToken(merchantId),
          setupBluesnapSuccess(merchantId, setData, history, handleErrors, getData),
          setupBluesnapError(setData, handleErrors),
        );
      })
      .catch(err => {
        console.log('### error loading BankAccount/Add', { err });
        notificationError('Page did not load properly. Please refresh.');
      })
      .finally(() => setLoadingStatus(false));
  }, [setData, merchantId, history, isSola, provider]);

  const handleSubmit = () => {
    setErrors([]);
    setSavingStatus(true);
    updateDataBeforeSubmission(setData);
    // Submission is NOT triggered here. updateDataBeforeSubmission appends a uuid to
    // businessName and sets isSubmitting, both via a React state update, so reading
    // dataRef.current now would give the pre-update values -- a dbaName without its
    // uniquifying suffix, which Sola rejects as a duplicate.
    //
    // BlueSnap sidesteps this by submitting from BluesnapFields' effect on isSubmitting.
    // The Sola effect below does the same.
  };

  // Sola has no SDK to hand submission to, so this effect plays that role: it waits for the
  // isSubmitting state update to commit, then submits with the settled data.
  useEffect(() => {
    if (!isSola || !data?.isSubmitting) {
      return;
    }

    submitSolaApplication(merchantId, setData, history, handleErrors, () => dataRef.current);
    // handleErrors is deliberately not a dependency: it is redefined every render, so
    // depending on it would resubmit. The isSubmitting guard above, plus
    // updateDataAfterSubmission clearing that flag, is what bounds this.
  }, [isSola, data?.isSubmitting, merchantId, history, setData]);

  if (!provider) {
    return null;
  }

  return (
    <Layout title="Add Bank Account" classProps="standard-width-with-sidebar align-left">
      <Errors errors={errors} />

      {data && (
        <Wizard
          data={data}
          setData={setData}
          onSubmit={handleSubmit}
          showAll={showAll}
          isSola={isSola}
          steps={[
            {
              title: 'Business Details',
              Component: BusinessDetails,
              helpText:
                'All stores will use this account by default unless you explicitly assign the store to another account.',
            },
            {
              title: 'Company Representative',
              Component: CompanyRep,
            },
            {
              title: 'Ownership',
              Component: Owners,
            },
            {
              title: 'Agreements',
              Component: Agreements,
            },
            {
              title: 'Bank Account',
              Component: AccountInfo,
            },
          ]}
        />
      )}
      {/* BlueSnap's hidden capture fields. Never rendered for Sola -- their only purpose is
          to be read by BlueSnap's SDK, which is not loaded on that path. */}
      {data && !isSola && <BluesnapFields data={data} />}
    </Layout>
  );
};

AddAccount.propTypes = {
  merchantId: oneOfType([number, string]).isRequired,
};

const mapStateToProps = state => ({
  merchantId: state.auth.merchantId,
});

export default connect(mapStateToProps)(AddAccount);
